import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Play } from "lucide-react";
import { API } from "@/lib/auth";

// `/d/:token` — a director's personal link: no access code, one tap, straight into the auto tour.
export default function ShareLinkPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [link, setLink] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.post(`${API}/share/${token}/open`)
      .then(({ data }) => setLink(data))
      .catch((e) => setError(e?.response?.data?.detail || "This link is no longer active"));
  }, [token]);

  const begin = () => {
    try {
      sessionStorage.setItem(`demo_unlock_${link.slug}`, "1");
      sessionStorage.setItem(`demo_share_${link.slug}`, JSON.stringify({ token, director_name: link.director_name }));
    } catch {}
    navigate(`/demos/${link.slug}?auto=1`);
  };

  return (
    <section data-testid="share-link-page" className="min-h-screen bg-[#0A0612] text-white flex items-center justify-center px-6 relative overflow-hidden">
      <div aria-hidden className="absolute inset-0" style={{ background: "radial-gradient(ellipse at 50% 45%, rgba(212,175,55,0.18), transparent 60%)" }} />
      {error && <p data-testid="share-link-error" className="relative text-slate-400 font-mono text-sm tracking-[0.2em] uppercase">{error}</p>}
      {link && (
        <motion.div className="relative text-center max-w-2xl" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
          <div className="font-mono text-[11px] tracking-[0.34em] uppercase text-[#C9A227]">A private demo for you</div>
          <h1 data-testid="share-link-welcome" className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight bg-gradient-to-b from-[#FFF4CC] via-[#F4D88A] to-[#C9A227] bg-clip-text text-transparent">
            Welcome, {link.director_name}
          </h1>
          {link.organization && <p className="mt-3 text-slate-300">{link.organization}</p>}
          <p className="mt-6 text-slate-400">Sound on. In about ten minutes you&apos;ll see how {link.business_name} moves people from crisis to self-sufficiency.</p>
          <motion.button type="button" onClick={begin} data-testid="share-link-begin"
            className="mt-10 inline-flex items-center gap-3 rounded-full bg-gradient-to-r from-[#C9A227] via-[#F4D88A] to-[#C9A227] px-10 py-5 text-lg font-extrabold text-[#0A0612] hover:brightness-110 active:scale-95 transition-[filter,transform]"
            animate={{ boxShadow: ["0 0 0 0 rgba(244,216,138,0.6)", "0 0 0 24px rgba(244,216,138,0)"] }} transition={{ duration: 1.8, repeat: Infinity }}>
            <Play className="w-5 h-5 fill-current" /> Tap to begin
          </motion.button>
        </motion.div>
      )}
    </section>
  );
}
